import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { exportToJSON, exportToHTML, parseNetscapeBookmarkHTML } from '../../lib/bookmarkIO';
import { downloadObsidianVault } from '../../lib/obsidianExporter';
import { exportStandaloneHtml } from '../../lib/standaloneExporter';

const { FiEdit3, FiCheck, FiDownload, FiUpload, FiHardDrive, FiCheckCircle2, FiFileText } = FiIcons;

export default function TabGeneral({
  settings,
  onSettingsChange,
  theme,
  data = {},
  onImportBookmarks,
}) {
  const { textColor, mutedText, inputBg, inputBorder, modalBorder, surfaceBg } = theme;
  const accent = (settings.themeColors?.accent || '#C2FFE0').toUpperCase();

  const [dashboardName, setDashboardName] = useState(settings.dashboardName || 'My Space');
  const [isEditing, setIsEditing] = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const fileInputRef = useRef(null);

  const [dockConfig, setDockConfig] = useState(() => {
    try {
      const saved = localStorage.getItem('memark_dock_config');
      return saved ? JSON.parse(saved) : {
        settings: true,
        vault: true,
        reading: true,
        notes: true,
        graph: true,
        scratchpad: true,
        sound: true,
        zen: true,
        shortcuts: true,
      };
    } catch {
      return {
        settings: true,
        vault: true,
        reading: true,
        notes: true,
        graph: true,
        scratchpad: true,
        sound: true,
        zen: true,
        shortcuts: true,
      };
    }
  });

  const toggleDockItem = (key) => {
    setDockConfig(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem('memark_dock_config', JSON.stringify(next));
      window.dispatchEvent(new Event('memark_dock_updated'));
      return next;
    });
  };

  const handleSave = () => {
    onSettingsChange({ ...settings, dashboardName: dashboardName || 'My Space' });
    setIsEditing(false);
  };

  const handleExportObsidian = () => {
    const allItems = Object.values(data).flatMap(f => f.items || []).filter(i => i.type === 'link');
    const folders = Object.values(data).map(f => ({ id: f.id, name: f.name || f.title || 'Folder' }));
    downloadObsidianVault(allItems, folders);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      const text = evt.target?.result;
      if (typeof text !== 'string') return;

      try {
        const parsed = parseNetscapeBookmarkHTML(text);
        if (onImportBookmarks) {
          await onImportBookmarks(parsed);
          setImportStatus(`Successfully imported ${parsed.rootBookmarks.length} root links & ${parsed.folders.length} folders!`);
          setTimeout(() => setImportStatus(''), 5000);
        }
      } catch (err) {
        console.error('Import error:', err);
        setImportStatus('Error importing bookmark file. Please check format.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <div className="space-y-6">
      {/* Section 1: Dashboard Name */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${accent}18`, border: `1px solid ${accent}33` }}
          >
            <SafeIcon icon={FiEdit3} className="w-4 h-4" style={{ color: accent }} />
          </div>
          <div>
            <p className="text-sm font-semibold" style={{ color: textColor }}>
              Dashboard Name
            </p>
            <p className="text-xs" style={{ color: mutedText }}>
              Personalize your space title
            </p>
          </div>
        </div>

        <div className="relative">
          <SafeIcon
            icon={FiEdit3}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4"
            style={{ color: mutedText }}
          />
          <input
            type="text"
            value={dashboardName}
            maxLength={30}
            onChange={(e) => {
              setDashboardName(e.target.value);
              setIsEditing(true);
            }}
            placeholder="Enter dashboard name…"
            className="w-full rounded-2xl px-11 py-3.5 text-sm focus:outline-none border-2 transition-all"
            style={{ backgroundColor: inputBg, borderColor: inputBorder, color: textColor }}
            onFocus={(e) => (e.currentTarget.style.borderColor = accent)}
            onBlur={(e) => (e.currentTarget.style.borderColor = inputBorder)}
          />
        </div>

        {isEditing && (
          <motion.button
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={handleSave}
            className="w-full py-3 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 transition-all"
            style={{ backgroundColor: accent, color: '#000000' }}
          >
            <SafeIcon icon={FiCheck} className="w-4 h-4" />
            Save Name
          </motion.button>
        )}
      </div>

      {/* Section 2: Data Management (Import & Export) */}
      <div className="pt-4 border-t" style={{ borderColor: modalBorder }}>
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-8 h-8 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${accent}18`, border: `1px solid ${accent}33` }}
          >
            <SafeIcon icon={FiHardDrive} className="w-4 h-4" style={{ color: accent }} />
          </div>
          <div>
            <p className="text-sm font-semibold" style={{ color: textColor }}>
              Data Management & Sovereign Backups
            </p>
            <p className="text-xs" style={{ color: mutedText }}>
              Export to Obsidian, JSON, or standard Netscape HTML
            </p>
          </div>
        </div>

        <div className="space-y-2.5">
          {/* Obsidian Vault Exporter */}
          <button
            onClick={handleExportObsidian}
            className="w-full py-2.5 px-3 rounded-xl border text-xs font-semibold flex items-center justify-center gap-2 transition-all hover:brightness-110"
            style={{
              backgroundColor: '#8b5cf618',
              borderColor: '#8b5cf635',
              color: '#a78bfa',
            }}
          >
            <SafeIcon icon={FiFileText} className="w-3.5 h-3.5 text-purple-400" />
            <span>Export Obsidian & Notion Vault (.md)</span>
          </button>

          {/* Standalone Single-File Offline Vault */}
          <button
            onClick={() => {
              const allItems = Object.values(data).flatMap(f => f.items || []).filter(i => i.type === 'link');
              const folders = Object.values(data).map(f => ({ id: f.id, name: f.name || f.title || 'Folder' }));
              exportStandaloneHtml(allItems, folders);
            }}
            className="w-full py-2.5 px-3 rounded-xl border text-xs font-semibold flex items-center justify-center gap-2 transition-all hover:brightness-110"
            style={{
              backgroundColor: '#10b98118',
              borderColor: '#10b98135',
              color: '#34d399',
            }}
          >
            <SafeIcon icon={FiHardDrive} className="w-3.5 h-3.5 text-emerald-400" />
            <span>Export Single-File Offline Vault (.html)</span>
          </button>

          <div className="flex gap-2">
            <button
              onClick={() => exportToJSON(data, settings)}
              className="flex-1 py-2.5 px-3 rounded-xl border text-xs font-semibold flex items-center justify-center gap-2 transition-all hover:brightness-110"
              style={{
                backgroundColor: surfaceBg,
                borderColor: inputBorder,
                color: textColor,
              }}
            >
              <SafeIcon icon={FiDownload} className="w-3.5 h-3.5" style={{ color: accent }} />
              Export JSON
            </button>

            <button
              onClick={() => exportToHTML(data)}
              className="flex-1 py-2.5 px-3 rounded-xl border text-xs font-semibold flex items-center justify-center gap-2 transition-all hover:brightness-110"
              style={{
                backgroundColor: surfaceBg,
                borderColor: inputBorder,
                color: textColor,
              }}
            >
              <SafeIcon icon={FiDownload} className="w-3.5 h-3.5" style={{ color: accent }} />
              Export HTML
            </button>
          </div>

          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".html,.htm"
              onChange={handleFileChange}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full py-2.5 px-3 rounded-xl border text-xs font-semibold flex items-center justify-center gap-2 transition-all hover:brightness-110"
              style={{
                backgroundColor: `${accent}15`,
                borderColor: `${accent}35`,
                color: accent,
              }}
            >
              <SafeIcon icon={FiUpload} className="w-3.5 h-3.5" />
              Import Bookmarks (HTML File)
            </button>
          </div>

          {importStatus && (
            <motion.div
              initial={{ opacity: 0, y: 2 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-[11px] p-2.5 rounded-xl border flex items-center gap-2"
              style={{
                backgroundColor: `${accent}10`,
                borderColor: `${accent}30`,
                color: textColor,
              }}
            >
              <SafeIcon icon={FiCheckCircle2} className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              <span>{importStatus}</span>
            </motion.div>
          )}
        </div>
      </div>

      {/* Section 3: Cyber Command Dock Customizer */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${accent}18`, border: `1px solid ${accent}33` }}
          >
            <SafeIcon icon={FiEdit3} className="w-4 h-4" style={{ color: accent }} />
          </div>
          <div>
            <p className="text-sm font-semibold" style={{ color: textColor }}>
              Command Dock Controls
            </p>
            <p className="text-xs" style={{ color: mutedText }}>
              Customize floating bottom toolbar visibility
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {[
            { id: 'settings', label: 'Settings' },
            { id: 'vault', label: 'Vault' },
            { id: 'reading', label: 'Reading' },
            { id: 'notes', label: 'Notes' },
            { id: 'graph', label: 'Graph' },
            { id: 'scratchpad', label: 'Scratchpad' },
            { id: 'sound', label: 'Audio FX' },
            { id: 'zen', label: 'Zen Mode' },
            { id: 'shortcuts', label: 'Shortcuts' },
          ].map((item) => {
            const isEnabled = dockConfig[item.id] !== false;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => toggleDockItem(item.id)}
                className={`p-2 rounded-xl border text-xs font-mono flex items-center justify-between transition-all cursor-pointer ${
                  isEnabled
                    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                    : theme.isDark
                    ? 'border-white/10 bg-white/5 text-[#666]'
                    : 'border-slate-200 bg-slate-100 text-slate-600'
                }`}
              >
                <span>{item.label}</span>
                <span className={`w-2 h-2 rounded-full ${isEnabled ? 'bg-emerald-400' : theme.isDark ? 'bg-white/20' : 'bg-slate-300'}`} />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}